/**
 * LOT #146 — télémétrie frontend : remonte au serveur les erreurs que l'ErrorBoundary
 * attrape et les rejets de promesse non gérés.
 *
 *   POST {API}/telemetry/frontend-errors   (authentifié, 10/min/session côté serveur)
 *
 * Motivation : l'écran « Une erreur est survenue » du 15/09 (agent4@danielcall2)
 * n'apparaissait nulle part côté serveur ; `reportBoundaryError` n'écrivait que
 * dans la console de l'iPad.
 *
 * Règles de conception (chacune a sa preuve dans `frontendErrorReporter.spec.ts`) :
 *  - `fetch` DIRECT, PAS `services/api.ts` : un rapport d'erreur ne doit jamais
 *    déclencher le mécanisme de refresh, un toast, ni — au bout de trois échecs
 *    d'auth — la DÉCONNEXION de l'agent (api.ts est la couche la plus fragile
 *    de l'app, on n'y touche pas et on n'en dépend pas) ;
 *  - JAMAIS de boucle : l'envoi attrape toutes ses erreurs, ne réessaie pas
 *    (sauf hors ligne : le rapport attend le retour réseau), ne journalise pas
 *    en `console.error` (un handler d'erreur ne doit pas relancer le sien) ;
 *  - file d'attente MÉMOIRE bornée, dédoublonnage (un crash de rendu qui boucle
 *    ne doit pas inonder le serveur), plafond client sous celui du serveur ;
 *  - aucune donnée de contact : URL sans query ni fragment ; le serveur masque
 *    en plus emails/numéros/jetons du texte libre.
 *
 * Les bornes reprennent `apps/backend/src/telemetry/telemetry.constants.ts` :
 * la `ValidationPipe` (forbidNonWhitelisted) refuse un champ trop long, un
 * rapport trop bavard serait donc perdu.
 */

export const FRONTEND_ERROR_LIMITS = {
  message: 500,
  stack: 4000,
  componentStack: 2000,
  url: 2000,
  userAgent: 300,
  version: 64,
} as const

export type FrontendErrorKind = 'render' | 'unhandledrejection'

export interface FrontendErrorInput {
  kind: FrontendErrorKind
  /** Valeur jetée / rejetée (Error, chaîne, objet quelconque). */
  error: unknown
  componentStack?: string | null
}

interface FrontendErrorPayload {
  message: string
  stack?: string
  componentStack?: string
  url: string
  userAgent: string
  version: string
  timestamp: string
  kind: FrontendErrorKind
}

export interface FrontendErrorReporterDeps {
  fetchImpl: (input: string, init: RequestInit) => Promise<Pick<Response, 'ok' | 'status'>>
  getToken: () => string | null
  isOnline: () => boolean
  now: () => number
  getUrl: () => string
  getUserAgent: () => string
  endpoint: string
  version: string
}

/** File mémoire : au-delà, le plus ancien rapport est abandonné. */
export const QUEUE_MAX = 20
/** Même erreur (message + début de stack) : un seul envoi par minute. */
export const DEDUPE_WINDOW_MS = 60_000
/** Plafond client : 8 envois par minute (le serveur coupe à 10). */
export const CLIENT_RATE_MAX = 8
export const CLIENT_RATE_WINDOW_MS = 60_000

export function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

/** `https://h/p?x=1#f` → `https://h/p`. */
export function stripQueryAndHash(raw: string): string {
  const cut = raw.search(/[?#]/)
  return cut >= 0 ? raw.slice(0, cut) : raw
}

/**
 * Message/stack d'une valeur quelconque, SANS sérialiser un objet arbitraire
 * (il pourrait contenir une fiche contact) : seuls `message` et `stack`
 * textuels d'un Error, ou une chaîne, sont repris.
 */
export function describeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message || error.name || 'Error', stack: error.stack }
  }
  if (typeof error === 'string') return { message: error || 'Erreur sans message' }
  if (typeof error === 'number' || typeof error === 'boolean') return { message: String(error) }
  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; stack?: unknown; name?: unknown }
    if (typeof candidate.message === 'string' && candidate.message) {
      return {
        message: candidate.message,
        stack: typeof candidate.stack === 'string' ? candidate.stack : undefined,
      }
    }
    const ctor = (error as { constructor?: { name?: unknown } }).constructor?.name
    return { message: `Valeur non-Error (${typeof ctor === 'string' ? ctor : 'Object'})` }
  }
  return { message: 'Erreur sans message' }
}

export function buildPayload(input: FrontendErrorInput, deps: FrontendErrorReporterDeps): FrontendErrorPayload {
  const { message, stack } = describeError(input.error)
  const payload: FrontendErrorPayload = {
    message: truncate(message, FRONTEND_ERROR_LIMITS.message),
    url: truncate(stripQueryAndHash(deps.getUrl()), FRONTEND_ERROR_LIMITS.url) || '/',
    userAgent: truncate(deps.getUserAgent(), FRONTEND_ERROR_LIMITS.userAgent),
    version: truncate(deps.version, FRONTEND_ERROR_LIMITS.version) || 'unknown',
    timestamp: new Date(deps.now()).toISOString(),
    kind: input.kind,
  }
  if (stack) payload.stack = truncate(stack, FRONTEND_ERROR_LIMITS.stack)
  if (input.componentStack) {
    payload.componentStack = truncate(input.componentStack, FRONTEND_ERROR_LIMITS.componentStack)
  }
  return payload
}

export interface FrontendErrorReporter {
  report(input: FrontendErrorInput): void
  /** Vide la file (sans jamais jeter). Résout quand l'envoi en cours est terminé. */
  flush(): Promise<void>
  /** Abonne `unhandledrejection` et `online` ; renvoie la désinscription. */
  install(win: Pick<Window, 'addEventListener' | 'removeEventListener'>): () => void
  /** Nombre de rapports en attente (tests / diagnostic). */
  pending(): number
}

export function createFrontendErrorReporter(deps: FrontendErrorReporterDeps): FrontendErrorReporter {
  const queue: FrontendErrorPayload[] = []
  const lastSeen = new Map<string, number>()
  let sentAt: number[] = []
  let flushing: Promise<void> | null = null

  const dedupeKey = (p: FrontendErrorPayload) =>
    `${p.kind}|${p.message}|${(p.stack ?? '').slice(0, 200)}`

  function underClientRate(): boolean {
    const cutoff = deps.now() - CLIENT_RATE_WINDOW_MS
    sentAt = sentAt.filter((t) => t > cutoff)
    return sentAt.length < CLIENT_RATE_MAX
  }

  async function drain(): Promise<void> {
    while (queue.length > 0) {
      // Pas de jeton (page de connexion, session expirée) : l'endpoint est
      // authentifié, un envoi ne ferait que provoquer un 401 inutile. On
      // abandonne ces rapports plutôt que de les garder indéfiniment.
      if (!deps.getToken()) {
        queue.length = 0
        return
      }
      // Hors ligne : on garde la file, `online` relancera l'envoi.
      if (!deps.isOnline()) return
      if (!underClientRate()) {
        queue.length = 0
        return
      }
      const item = queue.shift() as FrontendErrorPayload
      sentAt.push(deps.now())
      try {
        const token = deps.getToken()
        await deps.fetchImpl(deps.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(item),
          keepalive: true,
        })
        // Statut ignoré : 2xx = fait ; 4xx/5xx/429 = on n'insiste PAS (jamais
        // de boucle de réémission d'un rapport d'erreur).
      } catch {
        // Réseau coupé en plein envoi : on garde CE rapport pour le retour de
        // la connexion ; toute autre panne l'abandonne.
        if (!deps.isOnline()) {
          queue.unshift(item)
          return
        }
      }
    }
  }

  function flush(): Promise<void> {
    if (!flushing) {
      flushing = drain()
        .catch(() => undefined)
        .finally(() => {
          flushing = null
        })
    }
    return flushing
  }

  return {
    report(input) {
      try {
        const payload = buildPayload(input, deps)
        const key = dedupeKey(payload)
        const now = deps.now()
        const previous = lastSeen.get(key)
        if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) return
        lastSeen.set(key, now)
        if (lastSeen.size > 100) {
          for (const [k, t] of lastSeen) if (now - t >= DEDUPE_WINDOW_MS) lastSeen.delete(k)
          if (lastSeen.size > 100) lastSeen.clear()
        }
        queue.push(payload)
        while (queue.length > QUEUE_MAX) queue.shift()
        void flush()
      } catch {
        // La télémétrie ne doit JAMAIS faire tomber l'application.
      }
    },
    flush,
    install(win) {
      const onRejection = (event: Event) => {
        const reason = (event as PromiseRejectionEvent).reason
        this.report({ kind: 'unhandledrejection', error: reason })
      }
      const onOnline = () => {
        void flush()
      }
      win.addEventListener('unhandledrejection', onRejection)
      win.addEventListener('online', onOnline)
      return () => {
        win.removeEventListener('unhandledrejection', onRejection)
        win.removeEventListener('online', onOnline)
      }
    },
    pending: () => queue.length,
  }
}

// ---------------------------------------------------------------------------
// Instance applicative.
// ---------------------------------------------------------------------------

// Même expression que `services/api.ts` (non importé : voir en-tête).
const API_BASE_URL: string = import.meta.env?.VITE_API_URL || '/api'

function safeToken(): string | null {
  try {
    return localStorage.getItem('accessToken')
  } catch {
    return null
  }
}

let appReporter: FrontendErrorReporter | null = null

function getAppReporter(): FrontendErrorReporter {
  if (!appReporter) {
    appReporter = createFrontendErrorReporter({
      fetchImpl: (input, init) => fetch(input, init),
      getToken: safeToken,
      isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
      now: () => Date.now(),
      getUrl: () => (typeof window === 'undefined' ? '/' : window.location.href),
      getUserAgent: () => (typeof navigator === 'undefined' ? '' : navigator.userAgent),
      endpoint: `${API_BASE_URL.replace(/\/+$/, '')}/telemetry/frontend-errors`,
      // `__BUILD_ID__` est injecté par Vite (`define`) ; absent sous vitest.
      version: typeof __BUILD_ID__ === 'undefined' ? 'dev' : __BUILD_ID__,
    })
  }
  return appReporter
}

/** Point d'entrée UNIQUE pour l'ErrorBoundary (et tout appelant futur). Ne jette jamais. */
export function reportFrontendError(input: FrontendErrorInput): void {
  try {
    getAppReporter().report(input)
  } catch {
    // jamais
  }
}

/** Abonne `unhandledrejection` + `online` de la fenêtre. À appeler UNE fois (main.tsx). */
export function installFrontendErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => undefined
  try {
    return getAppReporter().install(window)
  } catch {
    return () => undefined
  }
}
